import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, RefreshCw, Trash2, Plus, Eye, EyeOff, CheckCircle2, Save, LogOut, CalendarDays, ShieldCheck } from "lucide-react";
import { API, ADMIN_KEY_STORAGE } from "@/lib/api";
import axios from "axios";
import { EVENT_TYPES, MOD_REASON_LABELS } from "@/lib/events";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { format } from "date-fns";
import { fr } from "date-fns/locale";

export default function Admin() {
  const [key, setKey] = useState(localStorage.getItem(ADMIN_KEY_STORAGE) || "");
  const [authed, setAuthed] = useState(false);
  const [tab, setTab] = useState("days");
  const [modCount, setModCount] = useState(0);

  const client = axios.create({ baseURL: API, headers: { "X-Admin-Key": key } });

  useEffect(() => {
    if (authed) client.get("/admin/moderation").then((r) => setModCount(r.data.length)).catch(() => {});
    // eslint-disable-next-line
  }, [authed, tab]);

  const login = async () => {
    try {
      await client.post("/admin/verify");
      localStorage.setItem(ADMIN_KEY_STORAGE, key);
      setAuthed(true);
      toast.success("Connecté");
    } catch {
      toast.error("Clé invalide");
    }
  };

  useEffect(() => {
    if (key) client.post("/admin/verify").then(() => setAuthed(true)).catch(() => {});
    // eslint-disable-next-line
  }, []);

  const logout = () => { localStorage.removeItem(ADMIN_KEY_STORAGE); setAuthed(false); setKey(""); };

  if (!authed) {
    return (
      <div className="min-h-screen bg-[#0B0F17] text-slate-100 flex items-center justify-center px-4">
        <div className="w-full max-w-sm bg-[#151C28] border border-slate-800 rounded-2xl p-6 space-y-4">
          <h1 className="font-black tracking-tight text-xl text-center">Alliance Manif · Administration</h1>
          <p className="text-xs text-slate-400 text-center">Accès protégé par clé (en-tête X-Admin-Key).</p>
          <input data-testid="admin-key-input" type="password" value={key} onChange={(e) => setKey(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && login()} placeholder="Clé d'administration"
            className="w-full h-12 px-3 rounded-xl bg-[#0B0F17] border border-slate-700 text-sm focus:outline-none focus:border-sky-500" />
          <button data-testid="admin-submit-btn" onClick={login} className="w-full h-12 rounded-xl bg-sky-600 hover:bg-sky-500 font-bold">Se connecter</button>
          <Link to="/" className="block text-center text-xs text-slate-400 hover:underline">Retour à l'application</Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-panel-container" className="min-h-screen bg-[#0B0F17] text-slate-100">
      <header className="bg-[#0B0F17]/95 border-b border-slate-800 sticky top-0 z-20 px-4 py-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Link to="/" className="h-10 w-10 flex items-center justify-center rounded-lg bg-slate-800 hover:bg-slate-700"><ArrowLeft size={18} /></Link>
          <h1 className="font-black tracking-tight text-base sm:text-lg">Alliance Manif · Administration</h1>
        </div>
        <button onClick={logout} className="h-10 px-3 rounded-lg bg-slate-800 hover:bg-slate-700 text-sm flex items-center gap-1.5"><LogOut size={16} /> Quitter</button>
      </header>

      <div className="px-4 py-2 border-b border-slate-800 flex gap-2 overflow-x-auto">
        {[["days", "Journées"], ["events", "Événements"], ["proposals", "Propositions"], ["moderation", "À modérer"], ["sources", "Sources RSS"], ["alerts", "Alertes"], ["logs", "Journal"]].map(([k, label]) => (
          <button key={k} data-testid={`admin-tab-${k}`} onClick={() => setTab(k)}
            className={`shrink-0 px-3 py-2 rounded-lg text-sm font-semibold inline-flex items-center gap-1.5 ${tab === k ? "bg-sky-600 text-white" : "bg-[#151C28] text-slate-300"}`}>
            {label}
            {k === "moderation" && modCount > 0 && <span data-testid="moderation-badge" className="text-[10px] font-extrabold px-1.5 py-0.5 rounded-full bg-amber-500 text-black">{modCount}</span>}
          </button>
        ))}
      </div>

      <div className="max-w-4xl mx-auto px-4 py-5">
        {tab === "days" && <DaysTab client={client} />}
        {tab === "events" && <EventsTab client={client} />}
        {tab === "proposals" && <ProposalsTab client={client} />}
        {tab === "moderation" && <ModerationTab client={client} />}
        {tab === "sources" && <SourcesTab client={client} />}
        {tab === "alerts" && <AlertsTab client={client} />}
        {tab === "logs" && <LogsTab client={client} />}
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <label className="block space-y-1"><span className="text-[11px] uppercase tracking-widest text-slate-500 font-bold">{label}</span>{children}</label>
);
const inputCls = "w-full h-11 px-3 rounded-lg bg-[#0B0F17] border border-slate-700 text-sm focus:outline-none focus:border-sky-500";

function DaysTab({ client }) {
  const [days, setDays] = useState([]);
  const [form, setForm] = useState({ name: "", date: "", description: "", keywords: "", status: "a_venir" });
  const load = () => client.get("/days").then((r) => setDays(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const save = async () => {
    if (!form.name || !form.date) return toast.error("Nom et date requis");
    await client.post("/admin/days", { ...form, keywords: form.keywords.split(",").map((k) => k.trim()).filter(Boolean) });
    toast.success("Journée créée");
    setForm({ name: "", date: "", description: "", keywords: "", status: "a_venir" });
    load();
  };
  const setStatus = async (d, status) => { await client.put(`/admin/days/${d.id}`, { ...d, status }); load(); };
  const remove = async (id) => { await client.delete(`/admin/days/${id}`); load(); };

  return (
    <div className="space-y-5">
      <div className="bg-[#151C28] border border-slate-800 rounded-xl p-4 grid sm:grid-cols-2 gap-3">
        <Field label="Nom"><input data-testid="day-name-input" className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
        <Field label="Date"><Popover>
          <PopoverTrigger asChild>
            <button type="button" data-testid="day-date-input" className={inputCls + " flex items-center justify-between text-left"}>
              <span className={form.date ? "" : "text-slate-500"}>{form.date ? format(new Date(form.date + "T12:00:00"), "d MMMM yyyy", { locale: fr }) : "Choisir une date"}</span>
              <CalendarDays size={16} className="text-slate-400" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0 bg-[#0B0F17] border-slate-700" align="start">
            <Calendar mode="single" locale={fr} initialFocus
              selected={form.date ? new Date(form.date + "T12:00:00") : undefined}
              onSelect={(d) => d && setForm({ ...form, date: format(d, "yyyy-MM-dd") })} />
          </PopoverContent>
        </Popover></Field>
        <Field label="Description"><input className={inputCls} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
        <Field label="Mots-clés (séparés par virgule)"><input className={inputCls} value={form.keywords} onChange={(e) => setForm({ ...form, keywords: e.target.value })} /></Field>
        <button data-testid="day-create-btn" onClick={save} className="h-11 rounded-lg bg-sky-600 hover:bg-sky-500 font-bold flex items-center justify-center gap-2 sm:col-span-2"><Plus size={16} /> Créer la journée</button>
      </div>
      <div className="space-y-2">
        {days.map((d) => (
          <div key={d.id} className="bg-[#151C28] border border-slate-800 rounded-xl p-4 flex flex-wrap items-center justify-between gap-3">
            <div><p className="font-bold">{d.name}</p><p className="text-xs text-slate-400">{d.date} · {d.status}</p></div>
            <div className="flex items-center gap-2">
              <select value={d.status} onChange={(e) => setStatus(d, e.target.value)} className="h-9 px-2 rounded-lg bg-[#0B0F17] border border-slate-700 text-xs">
                <option value="a_venir">À venir</option><option value="en_cours">En cours</option><option value="terminee">Terminée</option><option value="archivee">Archivée</option>
              </select>
              <button onClick={() => remove(d.id)} className="h-9 w-9 flex items-center justify-center rounded-lg bg-red-950 text-red-400 hover:bg-red-900"><Trash2 size={15} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function EventsTab({ client }) {
  const [events, setEvents] = useState([]);
  const [days, setDays] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [form, setForm] = useState({ type: "manifestation", title: "", city: "", source_name: "", source_url: "", status: "prevu", reliability: "confirme", day_id: "" });
  const load = () => client.get("/admin/events", { params: statusFilter ? { status: statusFilter } : {} }).then((r) => setEvents(r.data));
  useEffect(() => { load(); client.get("/days").then((r) => setDays(r.data)); /* eslint-disable-next-line */ }, [statusFilter]);

  const create = async () => {
    if (!form.title || !form.source_url) return toast.error("Titre et source (URL) requis");
    if (!form.city) return toast.error("Ville requise pour le géocodage");
    try {
      await client.post("/admin/events", { ...form, day_id: form.day_id || null });
      toast.success("Événement ajouté");
      setForm({ ...form, title: "", city: "", source_url: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };
  const patch = async (id, body) => { await client.put(`/admin/events/${id}`, body); load(); };
  const remove = async (id) => { await client.delete(`/admin/events/${id}`); load(); };

  return (
    <div className="space-y-5">
      <div className="bg-[#151C28] border border-slate-800 rounded-xl p-4 grid sm:grid-cols-2 gap-3">
        <p className="sm:col-span-2 font-bold text-sm">Ajout manuel (source obligatoire)</p>
        <Field label="Type"><select className={inputCls} value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>{Object.entries(EVENT_TYPES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}</select></Field>
        <Field label="Journée"><select className={inputCls} value={form.day_id} onChange={(e) => setForm({ ...form, day_id: e.target.value })}><option value="">— Aucune —</option>{days.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}</select></Field>
        <Field label="Titre"><input data-testid="event-title-input" className={inputCls} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></Field>
        <Field label="Ville (géocodée via API Adresse)"><input data-testid="event-city-input" className={inputCls} value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></Field>
        <Field label="Nom de la source"><input className={inputCls} value={form.source_name} onChange={(e) => setForm({ ...form, source_name: e.target.value })} /></Field>
        <Field label="URL source (obligatoire)"><input data-testid="event-source-input" className={inputCls} value={form.source_url} onChange={(e) => setForm({ ...form, source_url: e.target.value })} /></Field>
        <Field label="Statut"><select className={inputCls} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}><option value="prevu">Prévu</option><option value="en_cours">En cours</option><option value="termine">Terminé</option></select></Field>
        <Field label="Fiabilité"><select className={inputCls} value={form.reliability} onChange={(e) => setForm({ ...form, reliability: e.target.value })}><option value="confirme">Confirmé</option><option value="annonce">Annonce</option><option value="reseaux_sociaux">Réseaux sociaux</option></select></Field>
        <button data-testid="event-create-btn" onClick={create} className="h-11 rounded-lg bg-sky-600 hover:bg-sky-500 font-bold flex items-center justify-center gap-2 sm:col-span-2"><Plus size={16} /> Ajouter l'événement</button>
      </div>

      <p className="text-xs text-slate-500">{events.length} événement(s)</p>
      <div className="flex items-center gap-2 text-xs">
        <span className="text-slate-500">Filtrer statut :</span>
        <select data-testid="admin-event-status-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="h-9 px-2 rounded-lg bg-[#0B0F17] border border-slate-700">
          <option value="">Tous (actifs)</option>
          <option value="prevu">Prévu</option>
          <option value="en_cours">En cours</option>
          <option value="termine">Terminé (historique)</option>
        </select>
      </div>
      <div className="space-y-2">
        {events.map((e) => (
          <div key={e.id} className={`bg-[#151C28] border rounded-xl p-3 flex flex-wrap items-center justify-between gap-2 ${e.hidden ? "border-slate-700 opacity-60" : "border-slate-800"}`}>
            <div className="min-w-0">
              <p className="font-semibold text-sm truncate">{e.title}</p>
              <p className="text-xs text-slate-400">{EVENT_TYPES[e.type]?.label} · {e.city} · {e.reliability} · {e.status}{e.is_proposal ? ` · ${e.proposal_count || 0} prop.` : ""}</p>
            </div>
            <div className="flex items-center gap-1.5">
              {e.status !== "termine"
                ? <button title="Marquer terminé" onClick={() => patch(e.id, { status: "termine" })} className="h-9 px-2 flex items-center justify-center rounded-lg bg-slate-800 text-slate-300 hover:bg-slate-700 text-xs">Terminer</button>
                : <button title="Réactiver" onClick={() => patch(e.id, { status: "en_cours" })} className="h-9 px-2 flex items-center justify-center rounded-lg bg-emerald-950 text-emerald-400 hover:bg-emerald-900 text-xs">Réactiver</button>}
              {e.reliability !== "confirme" && <button title="Valider (confirmé)" onClick={() => patch(e.id, { reliability: "confirme" })} className="h-9 w-9 flex items-center justify-center rounded-lg bg-blue-950 text-blue-400 hover:bg-blue-900"><CheckCircle2 size={15} /></button>}
              <button title={e.hidden ? "Afficher" : "Masquer"} onClick={() => patch(e.id, { hidden: !e.hidden })} className="h-9 w-9 flex items-center justify-center rounded-lg bg-slate-800 text-slate-300 hover:bg-slate-700">{e.hidden ? <Eye size={15} /> : <EyeOff size={15} />}</button>
              <button title="Supprimer" onClick={() => remove(e.id)} className="h-9 w-9 flex items-center justify-center rounded-lg bg-red-950 text-red-400 hover:bg-red-900"><Trash2 size={15} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ModerationTab({ client }) {
  const [alerts, setAlerts] = useState([]);
  const [log, setLog] = useState([]);
  const load = () => { client.get("/admin/moderation").then((r) => setAlerts(r.data)); client.get("/admin/moderation/log").then((r) => setLog(r.data)); };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  const decide = async (id, decision) => {
    await client.post(`/admin/moderation/${id}/decision`, { decision });
    toast.success(decision === "rejeter" ? "Propositions rejetées" : decision === "fusionner" ? "Fusionnées et validées" : "Validées");
    load();
  };
  const autoRun = async () => {
    const r = await client.post("/admin/moderation/auto-run");
    const s = r.data;
    toast.success(`Admin IA : ${s.alerts} alerte(s) traitée(s) — ${s.validees} validée(s), ${s.fusionnees} fusionnée(s), ${s.rejetees} rejetée(s)`);
    load();
  };
  const undo = async (id) => { await client.post(`/admin/moderation/log/${id}/undo`); toast.success("Décision annulée"); load(); };
  return (
    <div className="space-y-3">
      <button data-testid="mod-auto-run" onClick={autoRun} className="w-full h-11 rounded-lg bg-violet-700 hover:bg-violet-600 font-bold flex items-center justify-center gap-2">
        <ShieldCheck size={16} /> Lancer l'admin IA (traiter toutes les alertes)
      </button>
      <p className="text-xs text-slate-500">{alerts.length} alerte(s) en attente. Une proposition signalée ou rejetée ne compte jamais pour le seuil des 2 IP.</p>
      {alerts.length === 0 && <p className="text-sm text-slate-400 py-6 text-center" data-testid="moderation-empty">Aucune proposition à modérer.</p>}
      {alerts.map((a) => (
        <div key={a.id} data-testid={`moderation-alert-${a.id}`} className="bg-[#151C28] border border-amber-800/60 rounded-xl p-3 space-y-2">
          <div className="flex flex-wrap gap-1.5">
            {(a.reasons || []).map((r) => (
              <span key={r} className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-amber-950/80 text-amber-300 border border-amber-600/50">{MOD_REASON_LABELS[r] || r}</span>
            ))}
          </div>
          <div className="text-xs text-slate-400 space-y-0.5">
            {(a.points || []).map((p, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className="font-mono text-slate-500">{p.lat?.toFixed(3)}, {p.lng?.toFixed(3)}</span>
                <span>{p.city} {p.time}</span>
                <a href={`https://www.openstreetmap.org/?mlat=${p.lat}&mlon=${p.lng}#map=15/${p.lat}/${p.lng}`} target="_blank" rel="noopener noreferrer" className="text-sky-400 hover:underline">voir</a>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-3 gap-2">
            <button data-testid={`mod-valider-${a.id}`} onClick={() => decide(a.id, "valider")} className="h-9 rounded-lg bg-emerald-950 text-emerald-400 hover:bg-emerald-900 text-xs font-bold">Valider</button>
            <button data-testid={`mod-fusionner-${a.id}`} onClick={() => decide(a.id, "fusionner")} className="h-9 rounded-lg bg-sky-950 text-sky-400 hover:bg-sky-900 text-xs font-bold">Fusionner</button>
            <button data-testid={`mod-rejeter-${a.id}`} onClick={() => decide(a.id, "rejeter")} className="h-9 rounded-lg bg-red-950 text-red-400 hover:bg-red-900 text-xs font-bold">Rejeter</button>
          </div>
        </div>
      ))}
      {log.length > 0 && (
        <div className="pt-2">
          <p className="text-xs font-bold text-slate-300 mb-2">Journal de modération</p>
          <div className="space-y-1">
            {log.slice(0, 20).map((l) => (
              <div key={l.id} className="bg-[#151C28] border border-slate-800 rounded-lg p-2 flex items-center justify-between gap-2 text-xs">
                <span className="text-slate-400 truncate">
                  <span className="font-semibold text-slate-200">{l.decision}</span> · {l.by || "admin"} · {(l.reasons || []).map((r) => MOD_REASON_LABELS[r] || r).join(", ")} · {new Date(l.ts).toLocaleString("fr-FR")}{l.undone ? " · annulée" : ""}
                </span>
                {!l.undone && <button data-testid={`mod-undo-${l.id}`} onClick={() => undo(l.id)} className="shrink-0 h-7 px-2 rounded bg-slate-800 text-slate-300 hover:bg-slate-700 text-[11px]">Annuler</button>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ProposalsTab({ client }) {
  const [props, setProps] = useState([]);
  const [filter, setFilter] = useState("queue");
  const load = () => client.get("/admin/proposals").then((r) => setProps(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  const validate = async (id) => { await client.post(`/admin/proposals/${id}/validate`); toast.success("Point ajouté à la carte"); load(); };
  const remove = async (id) => { await client.delete(`/admin/proposals/${id}`); load(); };
  const isQueue = (p) => p.status === "en_attente" || p.status === "pending";
  const originLabel = (o) => o === "presse" ? "Veille presse" : o === "rss" ? "Agrégation RSS" : "Visiteur";
  const originColor = (o) => (o === "presse" || o === "rss") ? "bg-amber-950 text-amber-400" : "bg-sky-950 text-sky-400";
  const list = props.filter((p) => filter === "queue" ? isQueue(p) : filter === "published" ? p.status === "published" : true);
  const queueCount = props.filter(isQueue).length;
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-1.5 flex-wrap" data-testid="proposals-filter">
        {[["queue", `File d'attente (${queueCount})`], ["published", "Publiées"], ["all", "Toutes"]].map(([k, label]) => (
          <button key={k} data-testid={`proposals-filter-${k}`} onClick={() => setFilter(k)}
            className={`h-8 px-3 rounded-lg text-xs font-semibold ${filter === k ? "bg-amber-700 text-white" : "bg-[#151C28] text-slate-400 border border-slate-800"}`}>{label}</button>
        ))}
      </div>
      <p className="text-xs text-slate-500">Les propositions (veille presse, agrégation ou visiteurs) restent en attente : rien n'apparaît sur la carte tant que vous ne cliquez pas « Ajouter à la carte ».</p>
      {list.length === 0 && <p className="text-sm text-slate-400 py-6 text-center">Aucune proposition.</p>}
      {list.map((p) => (
        <div key={p.id} data-testid={`proposal-row-${p.id}`} className="bg-[#151C28] border border-slate-800 rounded-xl p-3 flex flex-wrap items-center justify-between gap-2">
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${originColor(p.origin)}`}>{originLabel(p.origin)}</span>
              <p className="font-semibold text-sm truncate">{p.description || p.lieu || p.city}</p>
            </div>
            <p className="text-xs text-slate-400">{p.city} · {p.date} {p.time} · {p.status}{p.source_name ? ` · ${p.source_name}` : ""}{p.source_url ? <> · <a href={p.source_url} target="_blank" rel="noreferrer" className="text-sky-400 underline">source</a></> : ""}</p>
          </div>
          <div className="flex items-center gap-1.5">
            {p.status !== "published" && <button data-testid={`proposal-validate-${p.id}`} onClick={() => validate(p.id)} className="h-9 px-2 rounded-lg bg-sky-950 text-sky-400 hover:bg-sky-900 text-xs font-semibold">Ajouter à la carte</button>}
            <button data-testid={`proposal-delete-${p.id}`} onClick={() => remove(p.id)} className="h-9 w-9 flex items-center justify-center rounded-lg bg-red-950 text-red-400 hover:bg-red-900"><Trash2 size={15} /></button>
          </div>
        </div>
      ))}
    </div>
  );
}

function PressWatchPanel({ client }) {
  const [st, setSt] = useState({ last_run: null, per_source: {}, created: 0 });
  const [busy, setBusy] = useState(false);
  const load = () => client.get("/admin/press-watch/status").then((r) => setSt(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  const run = async () => {
    setBusy(true);
    try { const r = await client.post("/admin/press-watch/run"); setSt(r.data); toast.success(`Veille presse : ${r.data.created} proposition(s) créée(s)`); }
    catch { toast.error("Échec de la veille presse"); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid="press-watch-panel" className="bg-[#151C28] border border-amber-900/40 rounded-xl p-4 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div>
          <p className="font-bold text-sm">Veille presse (propositions en modération)</p>
          <p className="text-xs text-slate-500">Dernier passage : {st.last_run ? new Date(st.last_run).toLocaleString("fr-FR") : "jamais"} · passage auto toutes les 30 min</p>
        </div>
        <button data-testid="press-watch-run-btn" onClick={run} disabled={busy} className="h-9 px-3 shrink-0 rounded-lg bg-amber-700 hover:bg-amber-600 text-xs font-bold flex items-center gap-1.5 disabled:opacity-60"><RefreshCw size={14} className={busy ? "animate-spin" : ""} /> Lancer la veille</button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {Object.entries(st.per_source || {}).map(([n, c]) => (
          <div key={n} className="bg-[#0B0F17] border border-slate-800 rounded-lg px-2 py-1.5 text-xs flex justify-between gap-2"><span className="truncate text-slate-300">{n}</span><span className="font-bold text-amber-400">{c}</span></div>
        ))}
      </div>
      <p className="text-xs text-slate-500">Les propositions détectées restent en attente de validation et ne sont jamais publiées automatiquement.</p>
    </div>
  );
}

function SourcesTab({ client }) {
  const [sources, setSources] = useState([]);
  const [form, setForm] = useState({ name: "", url: "", region: "national" });
  const load = () => client.get("/admin/sources").then((r) => setSources(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  const add = async () => { if (!form.name || !form.url) return toast.error("Nom et URL requis"); await client.post("/admin/sources", { ...form, enabled: true }); setForm({ name: "", url: "", region: "national" }); load(); };
  const toggle = async (s) => { await client.put(`/admin/sources/${s.id}`, { ...s, enabled: !s.enabled }); load(); };
  const remove = async (id) => { await client.delete(`/admin/sources/${id}`); load(); };
  const refresh = async () => { await client.post("/admin/aggregate"); toast.success("Agrégation lancée (arrière-plan). Actualisez dans ~1 min."); };
  return (
    <div className="space-y-5">
      <PressWatchPanel client={client} />
      <button data-testid="admin-aggregate-btn" onClick={refresh} className="w-full h-11 rounded-lg bg-emerald-700 hover:bg-emerald-600 font-bold flex items-center justify-center gap-2"><RefreshCw size={16} /> Lancer l'agrégation maintenant (RSS + sources)</button>
      <div className="bg-[#151C28] border border-slate-800 rounded-xl p-4 grid sm:grid-cols-3 gap-3">
        <Field label="Nom"><input className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
        <Field label="URL du flux RSS"><input className={inputCls} value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} /></Field>
        <Field label="Région"><select className={inputCls} value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })}><option value="national">National</option><option value="regional">Régional</option><option value="officiel">Officiel</option></select></Field>
        <button onClick={add} className="h-11 rounded-lg bg-sky-600 hover:bg-sky-500 font-bold flex items-center justify-center gap-2 sm:col-span-3"><Plus size={16} /> Ajouter la source</button>
      </div>
      <div className="space-y-2">
        {sources.map((s) => (
          <div key={s.id} className="bg-[#151C28] border border-slate-800 rounded-xl p-3 flex items-center justify-between gap-2">
            <div className="min-w-0"><p className="font-semibold text-sm truncate">{s.name}</p><p className="text-xs text-slate-500 truncate">{s.url}</p></div>
            <div className="flex items-center gap-1.5 shrink-0">
              <button onClick={() => toggle(s)} className={`h-9 px-3 rounded-lg text-xs font-bold ${s.enabled ? "bg-emerald-950 text-emerald-400" : "bg-slate-800 text-slate-400"}`}>{s.enabled ? "Actif" : "Inactif"}</button>
              <button onClick={() => remove(s.id)} className="h-9 w-9 flex items-center justify-center rounded-lg bg-red-950 text-red-400 hover:bg-red-900"><Trash2 size={15} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function AlertsTab({ client }) {
  const [alerts, setAlerts] = useState([]);
  const [form, setForm] = useState({ text: "", source_name: "", source_url: "" });
  const load = () => client.get("/alerts").then((r) => setAlerts(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  const add = async () => { if (!form.text) return toast.error("Texte requis"); await client.post("/admin/alerts", form); setForm({ text: "", source_name: "", source_url: "" }); load(); };
  const remove = async (id) => { await client.delete(`/admin/alerts/${id}`); load(); };
  return (
    <div className="space-y-5">
      <div className="bg-[#151C28] border border-slate-800 rounded-xl p-4 grid gap-3">
        <Field label="Texte de l'alerte"><input className={inputCls} value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} /></Field>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Source"><input className={inputCls} value={form.source_name} onChange={(e) => setForm({ ...form, source_name: e.target.value })} /></Field>
          <Field label="URL source"><input className={inputCls} value={form.source_url} onChange={(e) => setForm({ ...form, source_url: e.target.value })} /></Field>
        </div>
        <button onClick={add} className="h-11 rounded-lg bg-sky-600 hover:bg-sky-500 font-bold flex items-center justify-center gap-2"><Plus size={16} /> Ajouter l'alerte</button>
      </div>
      <div className="space-y-2">
        {alerts.map((a) => (
          <div key={a.id} className="bg-[#151C28] border border-slate-800 rounded-xl p-3 flex items-center justify-between gap-2">
            <p className="text-sm min-w-0">{a.text} <span className="text-xs text-slate-500">— {a.source_name}</span></p>
            <button onClick={() => remove(a.id)} className="h-9 w-9 shrink-0 flex items-center justify-center rounded-lg bg-red-950 text-red-400 hover:bg-red-900"><Trash2 size={15} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function LogsTab({ client }) {
  const [logs, setLogs] = useState([]);
  useEffect(() => { client.get("/admin/logs").then((r) => setLogs(r.data)); /* eslint-disable-next-line */ }, []);
  return (
    <div className="space-y-2">
      {logs.length === 0 && <p className="text-sm text-slate-400">Aucun import pour le moment.</p>}
      {logs.map((l) => (
        <div key={l.id} className="bg-[#151C28] border border-slate-800 rounded-xl p-3 text-xs font-mono text-slate-300">
          <p className="text-slate-100 font-bold">{l.trigger} · {new Date(l.started_at).toLocaleString("fr-FR")}</p>
          <p>Sources: {l.sources_count} · Scannés: {l.scanned} · Créés: {l.created}</p>
          {l.errors?.length > 0 && <p className="text-amber-400">Erreurs: {l.errors.join(", ")}</p>}
        </div>
      ))}
    </div>
  );
}
